import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
	computeViewportDropdownPosition,
	getViewportMetrics,
	isTriggerRectVisible,
} from './computeViewportDropdownPosition'

const stylesEqual = (a, b) =>
	a &&
	b &&
	a.top === b.top &&
	a.bottom === b.bottom &&
	a.left === b.left &&
	a.minWidth === b.minWidth &&
	a.maxHeight === b.maxHeight &&
	a.overflowY === b.overflowY &&
	a.placement === b.placement

const measureMenuHeight = (menuNode, estimatedMenuHeight) => {
	if (!menuNode) return estimatedMenuHeight
	return menuNode.scrollHeight || menuNode.offsetHeight || estimatedMenuHeight
}

export const useViewportDropdown = ({
	isOpen,
	onClose,
	estimatedMenuHeight = 120,
	minMenuWidth = 148,
	viewportPadding,
	gap,
	preferredPlacement = 'bottom',
	horizontalAlign = 'start',
	menuZIndex = 2000,
	isOutsideClickIgnored,
}) => {
	const [menuStyle, setMenuStyle] = useState(null)
	const triggerRef = useRef(null)
	const menuRef = useRef(null)
	const onCloseRef = useRef(onClose)
	const isOutsideClickIgnoredRef = useRef(isOutsideClickIgnored)

	useEffect(() => {
		onCloseRef.current = onClose
	}, [onClose])

	useEffect(() => {
		isOutsideClickIgnoredRef.current = isOutsideClickIgnored
	}, [isOutsideClickIgnored])

	const updateMenuPosition = useCallback(() => {
		const trigger = triggerRef.current
		if (!trigger) return

		const triggerRect = trigger.getBoundingClientRect()
		const viewport = getViewportMetrics()

		if (
			!isTriggerRectVisible(
				triggerRect,
				viewport.viewportWidth,
				viewport.viewportHeight,
				viewport.viewportOffsetTop,
			)
		) {
			onCloseRef.current?.()
			return
		}

		const menuWidth = Math.max(triggerRect.width, minMenuWidth)
		const measuredHeight = measureMenuHeight(menuRef.current, estimatedMenuHeight)

		const nextStyle = computeViewportDropdownPosition({
			triggerRect,
			menuHeight: measuredHeight,
			menuWidth,
			viewportPadding,
			gap,
			preferredPlacement,
			horizontalAlign,
			...viewport,
			menuZIndex,
		})

		setMenuStyle(current => (stylesEqual(current, nextStyle) ? current : nextStyle))
	}, [
		estimatedMenuHeight,
		gap,
		horizontalAlign,
		minMenuWidth,
		preferredPlacement,
		viewportPadding,
		menuZIndex,
	])

	useLayoutEffect(() => {
		if (!isOpen) {
			setMenuStyle(null)
			return undefined
		}

		updateMenuPosition()
		const frameOne = requestAnimationFrame(() => {
			updateMenuPosition()
			requestAnimationFrame(updateMenuPosition)
		})

		const menuNode = menuRef.current
		const resizeObserver =
			typeof ResizeObserver !== 'undefined' && menuNode
				? new ResizeObserver(() => updateMenuPosition())
				: null
		if (menuNode && resizeObserver) {
			resizeObserver.observe(menuNode)
		}

		const visualViewport = window.visualViewport
		visualViewport?.addEventListener('resize', updateMenuPosition)
		visualViewport?.addEventListener('scroll', updateMenuPosition)
		window.addEventListener('resize', updateMenuPosition)
		window.addEventListener('scroll', updateMenuPosition, true)

		return () => {
			cancelAnimationFrame(frameOne)
			resizeObserver?.disconnect()
			visualViewport?.removeEventListener('resize', updateMenuPosition)
			visualViewport?.removeEventListener('scroll', updateMenuPosition)
			window.removeEventListener('resize', updateMenuPosition)
			window.removeEventListener('scroll', updateMenuPosition, true)
		}
	}, [isOpen, updateMenuPosition])

	useEffect(() => {
		if (!isOpen) return undefined

		const onDocClick = event => {
			const target = event.target
			if (!target) return
			if (triggerRef.current?.contains(target)) return
			if (menuRef.current?.contains(target)) return
			if (isOutsideClickIgnoredRef.current?.(target)) return
			onCloseRef.current?.()
		}

		const onKey = event => {
			if (event.key === 'Escape') onCloseRef.current?.()
		}

		document.addEventListener('mousedown', onDocClick)
		document.addEventListener('keydown', onKey)

		return () => {
			document.removeEventListener('mousedown', onDocClick)
			document.removeEventListener('keydown', onKey)
		}
	}, [isOpen])

	return {
		triggerRef,
		menuRef,
		menuStyle,
		updateMenuPosition,
		isReady: Boolean(menuStyle),
	}
}
